import { Staff } from '@/features/companies/types';

/**
 * Returns whether the staff belongs to any of the given department codes.
 * Matches primary `department.code`, `bucode`, or any `relatedStaffDepartments` entry.
 */
export function isStaffInSelectedDepartments(staff: Staff, selectedCodes: number[]): boolean {
  if (selectedCodes.length === 0) return false;
  const codeSet = new Set(selectedCodes);
  const primaryCode = staff.department?.code;
  if (primaryCode != null && codeSet.has(primaryCode)) return true;
  if (staff.bucode != null && codeSet.has(staff.bucode)) return true;
  if (staff.relatedStaffDepartments?.length) {
    return staff.relatedStaffDepartments.some(
      (related) => related.department?.code != null && codeSet.has(related.department.code)
    );
  }
  return false;
}

/**
 * Returns whether the staff is linked to a single department code (primary, bucode, or related).
 */
export function staffMatchesDepartmentCode(staff: Staff, code: number): boolean {
  const primaryCode = staff.department?.code;
  if (primaryCode != null && primaryCode === code) return true;
  if (staff.bucode != null && staff.bucode === code) return true;
  if (staff.relatedStaffDepartments?.length) {
    return staff.relatedStaffDepartments.some((related) => related.department?.code === code);
  }
  return false;
}

/**
 * Index of the first entry in `selectedCodes` that this staff matches.
 * Used to sort staff in the same order departments were selected; if none match, returns `selectedCodes.length`.
 */
export function getEarliestSelectedDepartmentIndex(staff: Staff, selectedCodes: number[]): number {
  const index = selectedCodes.findIndex((code) => staffMatchesDepartmentCode(staff, code));
  return index === -1 ? selectedCodes.length : index;
}

/**
 * Builds `{ id, label }` options for schedule delivery-staff pickers.
 * Filters by `selectedCodes`, then sorts by earliest matching selection index, then `lastName` / `firstName` ascending (Japanese collation).
 */
export function buildDeliveryStaffOptions(
  allDeliveryStaffs: Staff[],
  selectedCodes: number[]
): Array<{ id: string; label: string }> {
  if (selectedCodes.length === 0 || allDeliveryStaffs.length === 0) {
    return [];
  }

  const filteredStaffs = allDeliveryStaffs.filter((staff) =>
    isStaffInSelectedDepartments(staff, selectedCodes)
  );

  const sortedStaffs = [...filteredStaffs].sort((a, b) => {
    const indexA = getEarliestSelectedDepartmentIndex(a, selectedCodes);
    const indexB = getEarliestSelectedDepartmentIndex(b, selectedCodes);
    if (indexA !== indexB) return indexA - indexB;
    const byLast = a.lastName.localeCompare(b.lastName, 'ja', { sensitivity: 'base' });
    if (byLast !== 0) return byLast;
    return a.firstName.localeCompare(b.firstName, 'ja', { sensitivity: 'base' });
  });

  return sortedStaffs.map((staff) => ({
    id: staff.id,
    label: `${staff.lastName} ${staff.firstName}`,
  }));
}
